import { Link } from '@tanstack/react-router';
import { PRIVILEGES } from '@stayon/auth-core';
import { requiresPhotos, useSlotTemplateQuery } from '@/features/cleaning';
import { problemText } from '@/shared/api/problemText';
import { useApiProblem } from '@/shared/api/useApiProblem';
import { can, useAuth } from '@/shared/auth';
import { Button, SourceSection } from '@/shared/components';
import { useOnline } from '@/shared/hooks/useOnline';

/**
 * Fotonachweis des Apartments: ob die Reinigung je Raum ein Foto aufnimmt oder nur „Erledigt“
 * meldet, und welche Räume die Liste hat. Lesen und ändern darf nur, wer Reinigungen zuteilt — der
 * Server sperrt die Raumliste für alle anderen, also fragt die Karte gar nicht erst.
 */
export function CleaningEvidenceCard({ propertyId }: { propertyId: string }) {
  const { user } = useAuth();
  const mayManage = can(user, PRIVILEGES.cleaningAssignmentsManage);
  const online = useOnline();
  const template = useSlotTemplateQuery(propertyId, { enabled: mayManage });
  const problem = useApiProblem(template.error);
  const items = [...(template.data?.items ?? [])].sort((a, b) => a.position - b.position);
  const photos = requiresPhotos(template.data);
  // Fotos verlangt der Server nur für Räume mit Foto; gemischte Listen zählen nur diese.
  const rooms = (photos ? items.filter((item) => item.requiresPhoto) : items).map((i) => i.text);

  return (
    <section className="border-hairline rounded-tile flex flex-col gap-3 border bg-white p-5">
      <SourceSection
        source="internal"
        subtitle="Nur in StayOn. Nach jeder Abreise entsteht daraus die Reinigungsaufgabe."
      />
      {!mayManage ? (
        <p className="text-ink-soft text-sm">
          Die Raumliste sieht und pflegt, wer Reinigungen zuteilt.
        </p>
      ) : template.data === undefined ? (
        template.isError ? (
          <div role="alert" className="flex flex-wrap items-center gap-3">
            <p className="min-w-0 flex-1 text-sm">
              {problemText(problem, 'Die Raumliste konnte nicht geladen werden.')}
            </p>
            <Button
              variant="secondary"
              className="min-h-11"
              loading={template.isFetching}
              onClick={() => void template.refetch()}
            >
              Erneut laden
            </Button>
          </div>
        ) : (
          <p role="status" className="text-ink-soft text-sm">
            {online
              ? 'Raumliste wird geladen …'
              : 'Keine Verbindung – die Raumliste lädt, sobald du wieder Netz hast.'}
          </p>
        )
      ) : (
        <div className="flex flex-col gap-1">
          <b className="text-[15px]">
            {template.data === null
              ? 'Noch keine Raumliste – Aufgaben entstehen ohne Fotos'
              : photos
                ? `Fotos verlangt · ${rooms.length} ${rooms.length === 1 ? 'Raum' : 'Räume'}`
                : 'Ohne Fotos – die Reinigung meldet nur „Erledigt“'}
          </b>
          {rooms.length > 0 && (
            <span className="text-ink-soft text-sm">
              {photos ? '' : 'Gespeicherte Räume: '}
              {rooms.join(', ')}
            </span>
          )}
          {template.isError && (
            <span className="text-ink-soft text-xs">
              Nachladen fehlgeschlagen – zu sehen ist der zuletzt geladene Stand.
            </span>
          )}
        </div>
      )}
      {mayManage && (
        <Link
          to="/properties/$propertyId/checklist"
          params={{ propertyId }}
          className="rounded-pill bg-forest flex min-h-11 items-center self-start px-4 text-sm font-bold text-white"
        >
          Raumliste und Fotos bearbeiten
        </Link>
      )}
    </section>
  );
}
